import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import api from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { Button } from "../components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Separator } from "../components/ui/separator";
import { ArrowLeft, Plus, Heart, HeartOff, Users, ImageOff, Trash2, Edit, Package, Pencil, Bell, BellOff, CalendarClock, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import { resolveImageUrl } from "../lib/api";
import ReleaseCard from "../components/ReleaseCard";
import AiEstimateSection from "../components/AiEstimateSection";
import AdminImageUpload from "../components/AdminImageUpload";
import PurchaseFormFields from "../components/PurchaseFormFields";
import EntryPhotoUpload from "../components/EntryPhotoUpload";
import AdminReleaseForm from "../components/AdminReleaseForm";
import PriceHistoryChart from "../components/PriceHistoryChart";
import RarityScoreBadge from "../components/RarityScoreBadge";

const emptyForm = {
  purchase_price: "", purchase_date: "", seller: "", condition: "Mint",
  packaging_condition: "Mint", storage_location: "", notes: "", quantity: "1",
};

function ReleaseHero({ release, onBack }) {
  return (
    <div className="relative h-72 sm:h-96 overflow-hidden">
      {release.model_image ? (
        <img src={resolveImageUrl(release.model_image)} alt={release.release_name} className="w-full h-full object-cover" />
      ) : (
        <div className="w-full h-full flex flex-col items-center justify-center bg-gradient-to-br from-zinc-800 to-zinc-900">
          <ImageOff className="w-16 h-16 text-zinc-600 mb-2" />
          <span className="text-lg text-zinc-600 font-mono">{release.khmg_number}</span>
        </div>
      )}
      <div className="absolute inset-0 bg-gradient-to-t from-zinc-950 via-zinc-950/60 to-transparent" />
      <button data-testid="back-btn" onClick={onBack} className="absolute top-4 left-4 bg-zinc-900/80 backdrop-blur-sm text-zinc-300 hover:text-white p-2 rounded-lg transition-colors">
        <ArrowLeft className="w-5 h-5" />
      </button>
      <div className="absolute top-4 right-4 flex gap-2">
        {release.is_chase && <span className="bg-amber-500 text-zinc-950 px-3 py-1 rounded-full text-xs font-bold uppercase">CHASE {release.chase_ratio}</span>}
        {release.is_blkltd && <span className="bg-purple-500 text-white px-3 py-1 rounded-full text-xs font-bold uppercase">BLKLTD</span>}
        {release.variant_type === "Exclusive" && <span className="bg-sky-500 text-white px-3 py-1 rounded-full text-xs font-bold uppercase">EXCLUSIVE</span>}
      </div>
    </div>
  );
}

function ReleaseStats({ release }) {
  return (
    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
      <div className="bg-zinc-900/60 backdrop-blur-xl border border-white/5 rounded-xl p-4">
        <p className="text-[11px] text-zinc-500 uppercase tracking-wider">Market Value</p>
        <p className="font-mono text-xl font-bold text-rose-500 mt-1">
          {release.estimated_market_value != null ? `$${release.estimated_market_value.toFixed(2)}` : 'TBD'}
        </p>
      </div>
      <div className="bg-zinc-900/60 backdrop-blur-xl border border-white/5 rounded-xl p-4">
        <p className="text-[11px] text-zinc-500 uppercase tracking-wider">Vehicle</p>
        <p className="font-mono text-lg font-bold text-zinc-300 mt-1">{release.vehicle}</p>
      </div>
      <div className="bg-zinc-900/60 backdrop-blur-xl border border-white/5 rounded-xl p-4">
        <p className="text-[11px] text-zinc-500 uppercase tracking-wider">Type</p>
        <p className="font-mono text-lg font-bold text-zinc-300 mt-1">{release.variant_type}</p>
      </div>
      <div className="bg-zinc-900/60 backdrop-blur-xl border border-white/5 rounded-xl p-4">
        <p className="text-[11px] text-zinc-500 uppercase tracking-wider">Community</p>
        <div className="flex items-center gap-3 mt-1">
          <span className="text-sm text-zinc-300"><Users className="w-3 h-3 inline mr-1" />{release.owners_count} owners</span>
          <span className="text-sm text-zinc-500"><Heart className="w-3 h-3 inline mr-1" />{release.wishlist_user_count}</span>
        </div>
      </div>
    </div>
  );
}

function PurchaseRecordCard({ entry, onEdit, onDelete, onPhotosChange }) {
  const qty = entry.quantity || entry.quantity_owned || 1;
  const photos = entry.photos || [];
  return (
    <div data-testid={`purchase-record-${entry.entry_id}`} className="bg-zinc-800/40 border border-zinc-700/30 rounded-lg p-3">
      <div className="flex items-center gap-4">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-3 flex-wrap text-xs">
            {entry.purchase_price != null && <span className="text-zinc-300">Paid: <span className="font-mono text-emerald-400">${entry.purchase_price}</span></span>}
            <span className="text-zinc-500">Qty: {qty}</span>
            <span className="text-zinc-500">{entry.condition}</span>
            {entry.packaging_condition && entry.packaging_condition !== "Mint" && <span className="text-zinc-600">Pkg: {entry.packaging_condition}</span>}
          </div>
          <div className="flex items-center gap-3 flex-wrap text-[11px] text-zinc-600 mt-1">
            {entry.seller && <span>From: {entry.seller}</span>}
            {entry.purchase_date && <span>{entry.purchase_date}</span>}
            {entry.storage_location && <span>@ {entry.storage_location}</span>}
            {entry.notes && <span className="italic truncate max-w-[200px]">{entry.notes}</span>}
          </div>
        </div>
        <div className="flex gap-1 flex-shrink-0">
          <button data-testid={`edit-purchase-${entry.entry_id}`} onClick={() => onEdit(entry)} className="p-1.5 text-zinc-500 hover:text-zinc-300 hover:bg-zinc-700 rounded transition-colors"><Edit className="w-3.5 h-3.5" /></button>
          <button data-testid={`delete-purchase-${entry.entry_id}`} onClick={() => onDelete(entry.entry_id)} className="p-1.5 text-zinc-500 hover:text-rose-400 hover:bg-zinc-700 rounded transition-colors"><Trash2 className="w-3.5 h-3.5" /></button>
        </div>
      </div>
      <EntryPhotoUpload
        entryId={entry.entry_id}
        photos={photos}
        onPhotosChange={(newPhotos) => onPhotosChange(entry.entry_id, newPhotos)}
      />
    </div>
  );
}

const DETAIL_FIELDS = [
  ["Catalog #", "khmg_number"], ["Colorway", "colorway"], ["Livery", "livery"],
  ["Rarity", "rarity"], ["Variant", "variant_name"], ["Base Model", "base_model"],
  ["Vehicle", "vehicle"], ["Status", "status"],
];

function ReleaseInfo({ release }) {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
      <div className="bg-zinc-900/60 backdrop-blur-xl border border-white/5 rounded-xl p-5 space-y-3">
        <h3 className="font-heading text-lg font-semibold text-zinc-100">Release Details</h3>
        <Separator className="bg-zinc-800/50" />
        {DETAIL_FIELDS.map(([label, key]) => release[key] && (
          <div key={label} className="flex justify-between py-1">
            <span className="text-sm text-zinc-500">{label}</span>
            <span className="text-sm text-zinc-200 font-medium">{release[key]}</span>
          </div>
        ))}
      </div>
      {release.notes && (
        <div className="bg-zinc-900/60 backdrop-blur-xl border border-white/5 rounded-xl p-5">
          <h3 className="font-heading text-lg font-semibold text-zinc-100 mb-2">Notes</h3>
          <Separator className="bg-zinc-800/50 mb-3" />
          <p className="text-sm text-zinc-400 leading-relaxed">{release.notes}</p>
        </div>
      )}
    </div>
  );
}

export default function ReleaseDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [release, setRelease] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [editEntry, setEditEntry] = useState(null);
  const [addForm, setAddForm] = useState({ ...emptyForm });
  const [editForm, setEditForm] = useState({});
  const [showEditRelease, setShowEditRelease] = useState(false);
  const [watching, setWatching] = useState(false);
  const [muted, setMuted] = useState(false);
  const [watchLoading, setWatchLoading] = useState(false);

  const fetchRelease = useCallback(async () => {
    try {
      const [{ data: rel }, watchRes] = await Promise.all([
        api.get(`/releases/${id}`),
        api.get("/price-watch").catch(() => ({ data: { watches: [] } })),
      ]);
      setRelease(rel);
      const w = (watchRes.data.watches || []).find((x) => x.release_id === id);
      setWatching(!!w);
      setMuted(!!w?.muted);
    } catch {
      toast.error("Release not found");
      navigate("/catalog");
    } finally {
      setLoading(false);
    }
  }, [id, navigate]);

  useEffect(() => { fetchRelease(); }, [fetchRelease]);

  const handleAddCollection = async () => {
    try {
      await api.post("/collection", {
        release_id: id,
        quantity: addForm.quantity ? parseInt(addForm.quantity) : 1,
        purchase_price: addForm.purchase_price ? parseFloat(addForm.purchase_price) : null,
        purchase_date: addForm.purchase_date || null,
        seller: addForm.seller || null,
        condition: addForm.condition,
        packaging_condition: addForm.packaging_condition,
        storage_location: addForm.storage_location || null,
        notes: addForm.notes || null,
      });
      toast.success("Added to collection!");
      setShowAddDialog(false);
      setAddForm({ ...emptyForm });
      fetchRelease();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    }
  };

  const handleDeleteEntry = async (entryId) => {
    try {
      await api.delete(`/collection/${entryId}`);
      toast.success("Purchase record removed");
      fetchRelease();
    } catch { toast.error("Failed to remove"); }
  };

  const handleEditEntry = (entry) => {
    setEditEntry(entry);
    setEditForm({
      quantity: entry.quantity || entry.quantity_owned || 1,
      purchase_price: entry.purchase_price ?? "",
      seller: entry.seller || "",
      condition: entry.condition || "Mint",
      packaging_condition: entry.packaging_condition || "Mint",
      storage_location: entry.storage_location || "",
      notes: entry.notes || "",
    });
  };

  const handleUpdateEntry = async () => {
    if (!editEntry) return;
    try {
      const payload = {};
      if (editForm.quantity) payload.quantity = parseInt(editForm.quantity);
      if (editForm.purchase_price !== undefined) payload.purchase_price = editForm.purchase_price ? parseFloat(editForm.purchase_price) : null;
      if (editForm.seller !== undefined) payload.seller = editForm.seller || null;
      if (editForm.condition) payload.condition = editForm.condition;
      if (editForm.packaging_condition) payload.packaging_condition = editForm.packaging_condition;
      if (editForm.storage_location !== undefined) payload.storage_location = editForm.storage_location || null;
      if (editForm.notes !== undefined) payload.notes = editForm.notes || null;
      await api.put(`/collection/${editEntry.entry_id}`, payload);
      toast.success("Updated");
      setEditEntry(null);
      fetchRelease();
    } catch { toast.error("Failed to update"); }
  };

  const handlePhotosChange = (entryId, newPhotos) => {
    setRelease((prev) => ({
      ...prev,
      collection_entries: prev.collection_entries.map((e) =>
        e.entry_id === entryId ? { ...e, photos: newPhotos } : e
      ),
    }));
  };

  const handleDeleteRelease = async () => {
    if (!window.confirm(`Delete "${release.release_name}"? This removes all collection entries, wishlist entries, and trade listings for this release.`)) return;
    try {
      await api.delete(`/admin/releases/${id}`);
      toast.success("Release deleted");
      navigate("/catalog");
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to delete"); }
  };

  const handleToggleWishlist = async () => {
    try {
      if (release.wishlisted) {
        const { data } = await api.get("/wishlist");
        const entry = data.entries.find((e) => e.release_id === id);
        if (entry) await api.delete(`/wishlist/${entry.wishlist_id}`);
        toast.success("Removed from wishlist");
      } else {
        await api.post("/wishlist", { release_id: id, priority: "Medium" });
        toast.success("Added to wishlist!");
      }
      fetchRelease();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    }
  };

  const handleToggleWatch = async () => {
    if (watchLoading) return;
    setWatchLoading(true);
    try {
      const { data } = await api.post(`/price-watch/${id}`);
      setWatching(!!data.watching);
      setMuted(!!data.muted);
      toast.success(data.watching ? "Watching price" : "Stopped watching");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to toggle watch");
    } finally {
      setWatchLoading(false);
    }
  };

  const handleToggleMute = async () => {
    if (!watching || watchLoading) return;
    setWatchLoading(true);
    try {
      const { data } = await api.patch(`/price-watch/${id}/mute`, { muted: !muted });
      setMuted(!!data.muted);
      toast.success(data.muted ? "Alerts muted" : "Alerts unmuted");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to mute");
    } finally {
      setWatchLoading(false);
    }
  };

  if (loading) return <div className="p-4 sm:p-6 lg:p-8"><div className="h-80 bg-zinc-900 rounded-xl animate-pulse" /></div>;
  if (!release) return null;

  const entries = release.collection_entries || [];
  const totalQty = entries.reduce((s, e) => s + (e.quantity || e.quantity_owned || 1), 0);

  // A release is in preorder state when admin flagged it OR the release_date
  // is still in the future. Mirrors backend `is_preorder_doc` so the UI
  // matches what the catalog/preorders endpoints actually do.
  const releaseDateInFuture = release.release_date && new Date(release.release_date) > new Date();
  const isPreorder = release.is_preorder === true || releaseDateInFuture;
  const releaseDateStr = release.release_date
    ? new Date(release.release_date).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" })
    : null;

  return (
    <div data-testid="release-detail-page" className="animate-fadeIn">
      <ReleaseHero release={release} onBack={() => navigate(-1)} />

      <div className="p-4 sm:p-6 lg:p-8 -mt-20 relative z-10 space-y-6">
        {isPreorder && (
          <div
            data-testid="release-preorder-banner"
            className="rounded-xl bg-gradient-to-r from-fuchsia-500/15 via-rose-500/10 to-amber-500/15 border border-fuchsia-400/30 px-5 py-4 flex flex-col sm:flex-row sm:items-center gap-3 shadow-[0_0_24px_rgba(232,121,249,0.12)]"
          >
            <div className="flex items-center gap-3 flex-1 min-w-0">
              <div className="shrink-0 w-10 h-10 rounded-full bg-fuchsia-500/20 flex items-center justify-center">
                <CalendarClock className="w-5 h-5 text-fuchsia-200" />
              </div>
              <div className="min-w-0">
                <p className="text-fuchsia-100 font-semibold text-sm">
                  This release is currently a <span className="text-fuchsia-300">preorder</span>
                </p>
                <p className="text-zinc-400 text-xs mt-0.5">
                  Hidden from the main catalog until released.
                  {releaseDateStr ? <> Drops <span className="text-zinc-200 font-mono">{releaseDateStr}</span>.</> : null}
                </p>
              </div>
            </div>
            <div className="flex gap-2 flex-wrap">
              <Link
                to="/preorders"
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold bg-fuchsia-500/20 hover:bg-fuchsia-500/30 border border-fuchsia-400/40 text-fuchsia-100"
              >
                View in Future Releases →
              </Link>
              {release.preorder_url && (
                <a
                  href={release.preorder_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold bg-zinc-900/80 hover:bg-zinc-800 border border-zinc-700 text-zinc-200"
                >
                  <ExternalLink className="w-3 h-3" /> Preorder page
                </a>
              )}
            </div>
          </div>
        )}

        {/* Title + Actions */}
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
          <div>
            <p className="text-xs text-zinc-500 uppercase tracking-[0.2em] font-semibold">{release.khmg_number} &middot; {release.series}</p>
            <h1 className="font-heading text-2xl sm:text-3xl font-bold text-zinc-100 mt-1">{release.release_name}</h1>
            <p className="text-zinc-400 text-sm mt-1">{release.vehicle} &middot; {release.scale}</p>
            {isAdmin && (
              <div className="mt-3 flex items-center gap-2">
                <AdminImageUpload releaseId={id} currentImage={release.model_image} onImageUpdate={(url) => setRelease((prev) => ({ ...prev, model_image: url }))} />
                <Button data-testid="edit-release-btn" size="sm" variant="outline" onClick={() => setShowEditRelease(true)} className="bg-zinc-900 border-zinc-800 text-zinc-400 hover:text-zinc-200">
                  <Pencil className="w-3.5 h-3.5 mr-1" /> Edit
                </Button>
                <Button data-testid="delete-release-btn" size="sm" variant="outline" onClick={handleDeleteRelease} className="bg-zinc-900 border-zinc-800 text-zinc-500 hover:text-rose-400 hover:border-rose-500/30">
                  <Trash2 className="w-3.5 h-3.5 mr-1" /> Delete
                </Button>
              </div>
            )}
          </div>
          <div className="flex gap-2 flex-wrap">
            <Button data-testid="add-to-collection" onClick={() => setShowAddDialog(true)} className="bg-rose-600 hover:bg-rose-500 text-white shadow-[0_0_15px_rgba(225,29,72,0.3)]">
              <Plus className="w-4 h-4 mr-1" /> {release.owned ? "Add Another" : "Add to Collection"}
            </Button>
            <Button data-testid="toggle-wishlist" onClick={handleToggleWishlist} variant="outline" className={release.wishlisted ? "bg-rose-500/10 border-rose-500/30 text-rose-400" : "bg-zinc-900 border-zinc-800 text-zinc-400"}>
              {release.wishlisted ? <><HeartOff className="w-4 h-4 mr-1" /> Wishlisted</> : <><Heart className="w-4 h-4 mr-1" /> Wishlist</>}
            </Button>
            <Button
              data-testid="toggle-price-watch"
              onClick={handleToggleWatch}
              disabled={watchLoading}
              variant="outline"
              className={watching ? "bg-sky-500/10 border-sky-500/30 text-sky-400" : "bg-zinc-900 border-zinc-800 text-zinc-400"}
            >
              {watching ? <><Bell className="w-4 h-4 mr-1" /> Watching</> : <><Bell className="w-4 h-4 mr-1" /> Watch price</>}
            </Button>
            {watching && (
              <Button
                data-testid="toggle-mute-alerts"
                onClick={handleToggleMute}
                disabled={watchLoading}
                variant="outline"
                size="sm"
                className={muted ? "bg-zinc-800 border-zinc-700 text-zinc-400" : "bg-zinc-900 border-zinc-800 text-zinc-500"}
                title={muted ? "Alerts muted — click to unmute" : "Mute alerts for this release"}
              >
                {muted ? <><BellOff className="w-3.5 h-3.5 mr-1" /> Muted</> : <><BellOff className="w-3.5 h-3.5 mr-1" /> Mute</>}
              </Button>
            )}
          </div>
        </div>

        <ReleaseStats release={release} />

        {/* Rarity Score + Price History */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <RarityScoreBadge score={release.rarity_score} />
          <PriceHistoryChart releaseId={id} />
        </div>

        {/* Purchase Records */}
        {entries.length > 0 && (
          <div data-testid="purchase-records-section" className="bg-zinc-900/60 backdrop-blur-xl border border-white/5 rounded-xl p-5 space-y-3">
            <div className="flex items-center gap-2">
              <Package className="w-4 h-4 text-emerald-400" />
              <h3 className="font-heading text-lg font-semibold text-zinc-100">Your Purchases</h3>
              <span className="bg-emerald-500/10 text-emerald-400 border border-emerald-500/20 px-2 py-0.5 rounded-full text-[10px] font-medium">
                {entries.length} {entries.length === 1 ? "record" : "records"} &middot; {totalQty} total
              </span>
            </div>
            <div className="space-y-2">
              {entries.map((entry) => <PurchaseRecordCard key={entry.entry_id} entry={entry} onEdit={handleEditEntry} onDelete={handleDeleteEntry} onPhotosChange={handlePhotosChange} />)}
            </div>
          </div>
        )}

        <AiEstimateSection releaseId={id} />
        <ReleaseInfo release={release} />

        {/* Related Releases */}
        {release.related_releases?.length > 0 && (
          <div>
            <h3 className="font-heading text-xl font-semibold text-zinc-100 mb-4">Related Releases</h3>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
              {release.related_releases.map((r) => <ReleaseCard key={r.release_id} release={{ ...r, owned: false, wishlisted: false }} onCardClick={(rid) => navigate(`/catalog/${rid}`)} />)}
            </div>
          </div>
        )}
      </div>

      {/* Add Dialog */}
      <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
        <DialogContent className="bg-zinc-900 border-zinc-800 text-zinc-100 max-w-md">
          <DialogHeader><DialogTitle className="font-heading">{release.owned ? "Add Another Purchase" : "Add to Collection"}</DialogTitle></DialogHeader>
          <div className="space-y-4 mt-2">
            <PurchaseFormFields form={addForm} setForm={setAddForm} showDate showNotes />
            <Button data-testid="confirm-add-collection" onClick={handleAddCollection} className="w-full bg-rose-600 hover:bg-rose-500 text-white">
              {release.owned ? "Add Purchase Record" : "Add to Collection"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={!!editEntry} onOpenChange={() => setEditEntry(null)}>
        <DialogContent className="bg-zinc-900 border-zinc-800 text-zinc-100 max-w-md">
          <DialogHeader><DialogTitle className="font-heading">Edit Purchase Record</DialogTitle></DialogHeader>
          <div className="space-y-3 mt-2">
            <PurchaseFormFields form={editForm} setForm={setEditForm} showNotes />
            <Button onClick={handleUpdateEntry} className="w-full bg-rose-600 hover:bg-rose-500 text-white">Save Changes</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Admin Edit Release Dialog */}
      {isAdmin && (
        <AdminReleaseForm mode="edit" release={release} open={showEditRelease} onOpenChange={setShowEditRelease} onSuccess={fetchRelease} />
      )}
    </div>
  );
}
